import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useMemo, useState } from "react";
import { LayoutDashboard } from "lucide-react";

import { resolveCurrentAct } from "@/lib/scoreboard/acts";
import { getScoreboardShiftContext } from "@/lib/scoreboard/shift-context.functions";
import { getScoreboardHero, type ScoreboardHero } from "@/lib/scoreboard/hero.functions";
import {
  getScoreboardPulseRow,
  type PulseTile,
  type PulseTone,
} from "@/lib/scoreboard/pulse-row.functions";
import {
  getScoreboardMoments,
  type Moment,
} from "@/lib/scoreboard/moments.functions";
import {
  getScoreboardMomentum,
  type MomentumStat,
} from "@/lib/scoreboard/momentum.functions";
import { ScopeProvider, useScope } from "@/hooks/use-scope";

/**
 * Sprint 37 M1 — Scoreboard route & shell (the wall).
 *
 * Lives OUTSIDE the OperatorShell so the room is full-bleed with no chrome,
 * no nav, no filters. The Scoreboard is a broadcast, not a page — reference
 * mem://product/scoreboard-principles.
 *
 * Hero content lands in M3. This milestone establishes:
 *   • the full-bleed dark shell
 *   • the shift-clock header (act badge, shift name, wall time, day arc)
 *   • the three-act composition contract via resolveCurrentAct
 */
export const Route = createFileRoute("/_authenticated/scoreboard")({
  head: () => ({
    meta: [
      { title: "Scoreboard — CairnOS" },
      {
        name: "description",
        content:
          "The wall of the warehouse — how the shift is progressing, told in verdicts, sentences, and pulse.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ScoreboardPage,
});

function ScoreboardPage() {
  // Scoreboard sits outside the /app OperatorShell (which owns ScopeProvider),
  // so we mount our own provider here. Same scope contract, no chrome.
  return (
    <ScopeProvider>
      <ScoreboardRoom />
    </ScopeProvider>
  );
}

function ScoreboardRoom() {
  const now = useWallClock();
  const fetchContext = useServerFn(getScoreboardShiftContext);
  const fetchHero = useServerFn(getScoreboardHero);
  const fetchPulseRow = useServerFn(getScoreboardPulseRow);
  const fetchMoments = useServerFn(getScoreboardMoments);
  const fetchMomentum = useServerFn(getScoreboardMomentum);
  const { tenantId, facilityId } = useScope();

  // The shift context is server-authoritative and rarely changes. Poll at
  // an act-aware cadence once we have a resolved act; before the first
  // response, use a conservative interval so we don't hammer the server.
  const [refreshMs, setRefreshMs] = useState(60_000);
  const { data: ctx } = useQuery({
    queryKey: ["scoreboard", "shift-context"],
    queryFn: () => fetchContext(),
    refetchInterval: refreshMs,
    staleTime: 30_000,
  });

  const resolved = useMemo(() => {
    if (!ctx) return null;
    return resolveCurrentAct({
      now,
      shiftStart: new Date(ctx.shiftStartIso),
      shiftEnd: new Date(ctx.shiftEndIso),
      policy: ctx.policy,
    });
  }, [ctx, now]);

  useEffect(() => {
    if (resolved && resolved.refreshMs !== refreshMs) {
      setRefreshMs(resolved.refreshMs);
    }
  }, [resolved, refreshMs]);

  const { data: hero } = useQuery({
    queryKey: [
      "scoreboard",
      "hero",
      tenantId,
      facilityId,
      resolved?.act.id,
      // Round shift progress to 5% buckets so cache stays warm within an act.
      resolved ? Math.round(resolved.progress * 20) : 0,
    ],
    enabled: !!tenantId && !!facilityId && !!resolved,
    queryFn: () =>
      fetchHero({
        data: {
          tenantId: tenantId!,
          facilityId: facilityId!,
          actId: resolved!.act.id,
          shiftProgress: resolved!.progress,
        },
      }),
    refetchInterval: refreshMs,
    staleTime: 10_000,
  });

  const { data: pulses } = useQuery({
    queryKey: [
      "scoreboard",
      "pulse-row",
      tenantId,
      facilityId,
      resolved?.act.id,
      resolved ? Math.round(resolved.progress * 20) : 0,
    ],
    enabled: !!tenantId && !!facilityId && !!resolved,
    queryFn: () =>
      fetchPulseRow({
        data: {
          tenantId: tenantId!,
          facilityId: facilityId!,
          actId: resolved!.act.id,
          shiftProgress: resolved!.progress,
        },
      }),
    refetchInterval: refreshMs,
    staleTime: 10_000,
  });

  const { data: moments } = useQuery({
    queryKey: [
      "scoreboard",
      "moments",
      tenantId,
      facilityId,
      resolved?.act.id,
      ctx?.shiftStartIso,
      resolved ? Math.round(resolved.progress * 20) : 0,
    ],
    enabled: !!tenantId && !!facilityId && !!resolved && !!ctx,
    queryFn: () =>
      fetchMoments({
        data: {
          tenantId: tenantId!,
          facilityId: facilityId!,
          actId: resolved!.act.id,
          shiftStartIso: ctx!.shiftStartIso,
        },
      }),
    refetchInterval: refreshMs,
    staleTime: 10_000,
  });

  const { data: momentum } = useQuery({
    queryKey: ["scoreboard", "momentum", tenantId, facilityId],
    enabled: !!tenantId && !!facilityId,
    queryFn: () =>
      fetchMomentum({
        data: { tenantId: tenantId!, facilityId: facilityId! },
      }),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  if (!resolved || !ctx) {
    return <ScoreboardBooting now={now} />;
  }

  return (
    <div className="fixed inset-0 flex flex-col bg-background text-foreground">
      <ShiftClockHeader shiftName={ctx.shiftName} now={now} />
      <MomentumTicker stats={momentum} />
      <main className="flex flex-1 flex-col justify-between gap-10 px-12 pb-6 pt-6">
        <div className="flex flex-1 items-center justify-center">
          {hero ? (
            <HeroPanel hero={hero} question={resolved.act.question} />
          ) : (
            <HeroPlaceholder question={resolved.act.question} />
          )}
        </div>
        <PulseRow tiles={pulses} />
      </main>
      <MomentsTicker moments={moments} />
    </div>
  );
}

function ScoreboardBooting({ now }: { now: Date }) {
  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center gap-4 bg-background text-foreground">
      <div className="text-[10px] font-semibold uppercase tracking-[0.32em] text-muted-foreground">
        Scoreboard
      </div>
      <div className="text-2xl font-medium tracking-tight">Coming online</div>
      <div className="text-xs text-muted-foreground tabular-nums">
        {formatWallTime(now)}
      </div>
    </div>
  );
}

function ShiftClockHeader(props: { shiftName: string; now: Date }) {
  const wallTime = formatWallTime(props.now);
  const dayDate = formatDayDate(props.now);

  return (
    <header className="flex items-center justify-between gap-4 border-b border-border/40 px-4 sm:px-8 lg:px-12 py-2">
      <div className="flex min-w-0 items-center gap-2 truncate text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        <span className="text-foreground/80">{props.shiftName}</span>
        <span className="text-border">·</span>
        <span>{dayDate}</span>
      </div>
      <div className="flex shrink-0 items-center gap-3">
        <span className="text-[11px] tabular-nums text-muted-foreground">{wallTime}</span>
        <Link
          to="/app/operations/desk"
          reloadDocument
          className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border border-border/40 bg-background/40 px-2 py-0.5 text-[11px] text-muted-foreground transition-colors hover:bg-background/60 hover:text-foreground"
          title="Open Operations Workspace"
        >
          <LayoutDashboard className="h-3 w-3 shrink-0" />
          <span>Workspace</span>
        </Link>
      </div>
    </header>
  );
}

function HeroPlaceholder({ question }: { question: string }) {
  // M1 leaves the hero intentionally sparse. M3 replaces this with the
  // act-aware verdict + sentence + pulse composition. Until then, we render
  // just the shift's guiding question — a broadcast, not a placeholder card.
  return (
    <div className="flex max-w-4xl flex-col items-center gap-6 text-center">
      <div className="text-[10px] font-semibold uppercase tracking-[0.32em] text-muted-foreground">
        Now on the floor
      </div>
      <div className="text-6xl font-semibold leading-tight tracking-tight">{question}</div>
      <div className="text-sm text-muted-foreground">
        The wall is coming online. The hero panel lands next.
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// M3 — Hero panel: verdict + sentence + pulse
// ---------------------------------------------------------------------------

function HeroPanel({ hero, question }: { hero: ScoreboardHero; question: string }) {
  return (
    <div className="flex w-full max-w-6xl flex-col items-center gap-8 text-center">
      <div className="flex flex-col items-center gap-2">
        <div
          className="font-semibold uppercase tracking-[0.32em] text-muted-foreground"
          style={{ fontSize: "clamp(0.7rem, 0.7vw, 1rem)" }}
        >
          {question}
        </div>
        <Verdict verdict={hero.verdict} />
      </div>
      <p
        key={hero.sentence}
        className="max-w-4xl animate-fade-in font-medium leading-snug tracking-tight text-foreground"
        style={{ fontSize: "clamp(1.5rem, 2vw, 3rem)" }}
      >
        {hero.sentence}
      </p>
      <Pulse value={hero.pulse.value} label={hero.pulse.label} />
    </div>
  );
}

function Verdict({ verdict }: { verdict: ScoreboardHero["verdict"] }) {
  // Semantic tokens only — no bg-white / bg-black / hex colors. Tone maps to
  // the same health palette used elsewhere in the OS.
  const tone: "ok" | "watch" | "urgent" = verdictTone(verdict);
  const toneClass =
    tone === "urgent"
      ? "text-destructive"
      : tone === "watch"
        ? "text-warning-foreground"
        : "text-foreground";
  return (
    <div
      key={verdict}
      className={`animate-fade-in font-semibold uppercase leading-none tracking-tight ${toneClass}`}
      style={{ fontSize: "clamp(4rem, 8vw, 11rem)", letterSpacing: "-0.02em" }}
    >
      {verdict}
    </div>
  );
}

function verdictTone(v: ScoreboardHero["verdict"]): "ok" | "watch" | "urgent" {
  switch (v) {
    case "READY":
    case "ON TRACK":
    case "AHEAD":
    case "CLEAR":
    case "NAILED IT":
      return "ok";
    case "OPENING":
    case "WATCHING":
    case "CATCHING UP":
    case "TIGHT":
      return "watch";
    case "BEHIND":
    case "LATE":
      return "urgent";
  }
}

function Pulse({ value, label }: { value: number; label?: string }) {
  const pct = Math.max(0, Math.min(1, value)) * 100;
  return (
    <div className="flex w-full max-w-2xl flex-col gap-2">
      <div className="relative h-2 w-full overflow-hidden rounded-full bg-muted/40">
        <div
          className="h-full rounded-full bg-foreground/90 transition-[width] duration-1000 ease-out"
          style={{ width: `${Math.max(pct, 1)}%` }}
        />
      </div>
      {label && (
        <div className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
          {label}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// M4 — Pulse row: supporting evidence for the Hero's one story.
//
// Every tile follows the same grammar as the Hero — verdict + sentence +
// pulse. Tiles reorder by urgency but never disappear: the wall always
// shows the whole building. No numbers stand alone.
// ---------------------------------------------------------------------------

function PulseRow({ tiles }: { tiles: PulseTile[] | undefined }) {
  if (!tiles) {
    return (
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="h-32 rounded-xl border border-border/40 bg-muted/10"
            aria-hidden
          />
        ))}
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
      {tiles.map((tile) => (
        <PulseTileCard key={tile.area} tile={tile} />
      ))}
    </div>
  );
}

function PulseTileCard({ tile }: { tile: PulseTile }) {
  // Silence is part of the design — a calm tile shouldn't visually shout.
  const calm = tile.tone === "ok" && tile.urgency <= 6;
  const toneRing =
    tile.tone === "urgent"
      ? "border-destructive/50"
      : tile.tone === "watch"
        ? "border-warning/40"
        : calm
          ? "border-border/25"
          : "border-border/40";
  return (
    <div
      key={`${tile.area}-${tile.verdict}`}
      className={`flex animate-fade-in flex-col justify-between gap-3 rounded-xl border ${toneRing} bg-card/40 p-4 transition-colors duration-500`}
    >
      <div className="flex items-center justify-between">
        <span
          className="font-semibold uppercase tracking-[0.24em] text-muted-foreground"
          style={{ fontSize: "clamp(0.625rem, 0.55vw, 0.85rem)" }}
        >
          {tile.area}
        </span>
        <PulseDot tone={tile.tone} calm={calm} />
      </div>
      <div
        className={`font-semibold uppercase tracking-tight ${calm ? "text-foreground/80" : toneText(tile.tone)}`}
        style={{ fontSize: "clamp(1.15rem, 1.35vw, 2rem)" }}
      >
        {tile.verdict}
      </div>
      <p
        className="leading-snug text-foreground/85"
        style={{ fontSize: "clamp(0.75rem, 0.8vw, 1.1rem)" }}
      >
        {tile.sentence}
      </p>
      <MiniPulse value={tile.pulse.value} tone={tile.tone} label={tile.pulse.label} />
    </div>
  );
}

function PulseDot({ tone, calm }: { tone: PulseTone; calm?: boolean }) {
  // Urgent tiles get a gentle pulse; watch is steady; calm is barely there.
  const cls =
    tone === "urgent"
      ? "bg-destructive animate-pulse"
      : tone === "watch"
        ? "bg-warning"
        : calm
          ? "bg-emerald-500/40"
          : "bg-emerald-500/80";
  return (
    <span
      aria-hidden
      className={`h-1.5 w-1.5 rounded-full ${cls} shadow-[0_0_0_3px_rgba(255,255,255,0.04)]`}
    />
  );
}

function MiniPulse({
  value,
  tone,
  label,
}: {
  value: number;
  tone: PulseTone;
  label?: string;
}) {
  const pct = Math.max(0, Math.min(1, value)) * 100;
  const fill =
    tone === "urgent"
      ? "bg-destructive/80"
      : tone === "watch"
        ? "bg-warning/80"
        : "bg-foreground/85";
  return (
    <div className="flex flex-col gap-1.5">
      <div className="relative h-1 w-full overflow-hidden rounded-full bg-muted/40">
        <div
          className={`h-full rounded-full ${fill} transition-[width] duration-1000 ease-out`}
          style={{ width: `${Math.max(pct, 2)}%` }}
        />
      </div>
      {label && (
        <div className="text-[9px] uppercase tracking-[0.22em] text-muted-foreground">
          {label}
        </div>
      )}
    </div>
  );
}

function toneText(tone: PulseTone): string {
  return tone === "urgent"
    ? "text-destructive"
    : tone === "watch"
      ? "text-warning-foreground"
      : "text-foreground";
}

// ---------------------------------------------------------------------------
// M13 — Moments ticker: the warehouse's ambient highlight reel.
//
// Moments are earned, not generated. When the building hasn't earned one,
// the ticker hides entirely — silence is part of the design. When there
// are moments to broadcast, they scroll along the bottom of the wall like
// a stock or sports ticker, never competing with the Hero for attention.
// Subjects are always collective — a shift, a dock, a station, a customer,
// the warehouse — never a named person.
// ---------------------------------------------------------------------------

function MomentsTicker({ moments }: { moments: Moment[] | undefined }) {
  // Quiet by construction: no moments, no ticker. The wall doesn't
  // announce that it has nothing to announce.
  if (!moments || moments.length === 0) return null;

  // Duplicate the reel so the marquee loops seamlessly.
  const reel = [...moments, ...moments];
  // Slower with fewer moments; still calm with many. Keep between 45–90s.
  const durationSec = Math.min(90, Math.max(45, moments.length * 12));

  return (
    <aside
      aria-label="Moments"
      className="relative flex items-center gap-6 overflow-hidden border-t border-border/40 bg-card/30 px-8 py-4 backdrop-blur-sm"
    >
      <div
        className="shrink-0 font-semibold uppercase tracking-[0.32em] text-muted-foreground"
        style={{ fontSize: "clamp(0.625rem, 0.55vw, 0.85rem)" }}
      >
        Moments · Today
      </div>
      <div
        aria-hidden
        className="h-4 w-px shrink-0 bg-border/60"
      />
      <div className="relative flex-1 overflow-hidden">
        <div
          className="flex w-max animate-[scoreboard-ticker_var(--ticker-duration)_linear_infinite] gap-10"
          style={
            {
              // Custom property drives the keyframe duration; keeps CSS clean.
              "--ticker-duration": `${durationSec}s`,
            } as React.CSSProperties
          }
        >
          {reel.map((m, i) => (
            <MomentTickerItem key={`${m.id}-${i}`} moment={m} />
          ))}
        </div>
        {/* Soft edge fade so items enter and exit like a broadcast, not a slot machine. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-background to-transparent"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-background to-transparent"
        />
      </div>
    </aside>
  );
}

function MomentTickerItem({ moment }: { moment: Moment }) {
  return (
    <span className="flex shrink-0 items-baseline gap-3">
      <span aria-hidden className="text-foreground/60">
        {momentKindGlyph(moment.kind)}
      </span>
      <span
        className="font-semibold uppercase tracking-[0.24em] text-muted-foreground"
        style={{ fontSize: "clamp(0.625rem, 0.55vw, 0.85rem)" }}
      >
        {momentKindLabel(moment.kind)}
      </span>
      <span
        className="font-medium tracking-tight text-foreground"
        style={{ fontSize: "clamp(0.9rem, 0.95vw, 1.25rem)" }}
      >
        {moment.headline}
      </span>
    </span>
  );
}

function momentKindGlyph(kind: Moment["kind"]): string {
  switch (kind) {
    case "shipping-milestone":
      return "🏆";
    case "cutoff-cleared":
      return "✓";
    case "dock-cleared":
      return "◆";
    case "customer-milestone":
      return "★";
  }
}

function momentKindLabel(kind: Moment["kind"]): string {
  switch (kind) {
    case "shipping-milestone":
      return "Milestone";
    case "cutoff-cleared":
      return "Cutoff";
    case "dock-cleared":
      return "Dock";
    case "customer-milestone":
      return "Customer";
  }
}

// ---------------------------------------------------------------------------
// Momentum ticker — ESPN/stock-ticker style band between header and Hero.
// Positivity balance: shows forward motion even on a rough day.
// ---------------------------------------------------------------------------

function MomentumTicker({ stats }: { stats: MomentumStat[] | undefined }) {
  // Fixed grid — never scrolls, always readable. Six counters, one voice.
  const skeleton: MomentumStat[] = Array.from({ length: 6 }).map((_, i) => ({
    key: `s-${i}`,
    label: "—",
    value: "—",
  }));
  const cells = stats && stats.length > 0 ? stats : skeleton;
  return (
    <section
      aria-label="Momentum"
      className="border-b border-border/40 bg-card/20 px-4 sm:px-8 lg:px-12 py-5 sm:py-6"
    >
      <div className="grid grid-cols-3 gap-x-6 gap-y-5 md:grid-cols-6">
        {cells.map((s) => (
          <MomentumStatCell key={s.key} stat={s} />
        ))}
      </div>
    </section>
  );
}

function MomentumStatCell({ stat }: { stat: MomentumStat }) {
  return (
    <div className="flex min-w-0 flex-col items-center gap-1 text-center">
      <div
        className="font-semibold uppercase tracking-[0.24em] text-muted-foreground"
        style={{ fontSize: "clamp(0.65rem, 0.7vw, 0.9rem)" }}
      >
        {stat.label}
      </div>
      <div
        key={stat.value}
        className="animate-fade-in font-semibold tabular-nums leading-none tracking-tight text-foreground"
        style={{ fontSize: "clamp(2rem, 3.5vw, 4.5rem)", letterSpacing: "-0.02em" }}
      >
        {stat.value}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function useWallClock(): Date {
  const [now, setNow] = useState<Date>(() => new Date());
  useEffect(() => {
    const t = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(t);
  }, []);
  return now;
}

function formatWallTime(d: Date): string {
  return d.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function formatDayDate(d: Date): string {
  return d.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}